import { useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'

import { GrowthCategory, GrowthContent, GrowthLesson } from '@mc/api'
import { useBucket } from '@mc/experiments-react'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'
import { trackExperimentViewed } from '@mc/track-event'

import { LESSONS_ON_QR_EXP_NAME } from './utils'

export const useLessonsOnQRExperiment = () => {
  const bucket = useBucket(LESSONS_ON_QR_EXP_NAME)
  const { asPath } = useRouter()
  const isInQuizPage = asPath.includes(paths.classRecommendations)
  const isLessonsOnQRVariant1 = bucket === 'variant_1' && isInQuizPage
  const isLessonsOnQRVariant2 = bucket === 'variant_2' && isInQuizPage

  const trackQRSealedBucketWithLessonsViewed = useCallback(() => {
    trackExperimentViewed(LESSONS_ON_QR_EXP_NAME)
  }, [])

  return {
    isLessonsOnQRVariant1,
    isLessonsOnQRVariant2,
    trackQRSealedBucketWithLessonsViewed,
  }
}

export const useQuizResultsCategories = () => {
  const {
    categories,
    subcats,
    lessonResults: { mergedLessonsByCategory, allLessonCountByCategory },
  } = useStaticData<{
    categories: GrowthCategory[]
    subcats?: GrowthCategory[]
    lessonResults: {
      lessonClasses: GrowthContent[]
      allLessonCountByCategory: {
        [categoryId: string]: number
      }
      mergedLessonsByCategory: {
        categoryId: number
        lessons: GrowthLesson[]
      }[]
    }
  }>()

  const selectedCategories = useMemo(
    () =>
      categories
        .filter((c) =>
          mergedLessonsByCategory.find(
            (catWithLessons) => catWithLessons.categoryId === c.id,
          ),
        )
        .sort(
          (a, b) =>
            allLessonCountByCategory[b.id] - allLessonCountByCategory[a.id],
        ),
    [categories, mergedLessonsByCategory, allLessonCountByCategory],
  )

  const getSelectedSubcatsForCategory = useCallback(
    (categoryId: number) =>
      subcats?.filter((subcat) => subcat.parentId === categoryId) ?? [],
    [subcats],
  )

  return { selectedCategories, getSelectedSubcatsForCategory }
}

export const useQuizLessonResults = () => {
  const {
    lessonResults: {
      lessonClasses,
      mergedLessonsByCategory,
      allLessonCountByCategory,
    },
  } = useStaticData<{
    lessonResults: {
      lessonClasses: GrowthContent[]
      mergedLessonsByCategory: {
        categoryId: number
        lessons: GrowthLesson[]
      }[]
      allLessonCountByCategory: {
        [categoryId: string]: number
      }
    }
  }>()

  const getLessonClass = useCallback(
    (lesson: GrowthLesson) =>
      lessonClasses.find((c) => c.uuid === lesson.class.uuid),
    [lessonClasses],
  )

  const getLessonResultsForCategoryId = useCallback(
    (categoryId: number) =>
      mergedLessonsByCategory.find((c) => c.categoryId === categoryId)
        ?.lessons ?? [],
    [mergedLessonsByCategory],
  )

  const getAllLessonCountForCategoryId = useCallback(
    (categoryId: number) => allLessonCountByCategory[String(categoryId)] ?? 0,
    [allLessonCountByCategory],
  )

  return {
    getLessonClass,
    getLessonResultsForCategoryId,
    getAllLessonCountForCategoryId,
  }
}
